import React, { useEffect, useMemo, useState } from 'react';
import AnimatedTitle from '../AnimatedTitle';
import AudioPlayer from '../AudioPlayer';
import '../improvimania/HostScreen.css';
import '../improvimania/HostScreen/Lobby.css';
import './setthebar.css';
import setTheBarTheme from '../../sound/setthebar/theme.mp3';

const defaultSettings = {
  questionCount: 10,
  questionDurationSec: 20,
  packId: 'all',
};

const sortScoreboard = (scoreboard = []) => [...scoreboard].sort((a, b) => b.iq - a.iq);

const formatIq = (value) => {
  const rounded = Number(value || 0);
  if (Math.abs(rounded - Math.round(rounded)) < 0.01) {
    return String(Math.round(rounded));
  }
  return rounded.toFixed(1);
};

export default function SetTheBarHost({
  socket,
  sessionId,
  players,
  ipAddress,
  serverType,
  removePlayer,
  setForceRemove,
  onReturnToPartyPack,
  isMusicMuted = false,
}) {
  const [state, setState] = useState({
    phase: 'lobby',
    settings: defaultSettings,
    availablePacks: [],
    currentQuestion: null,
    review: null,
    progress: { questionIndex: 0, totalQuestions: 0, intermissionEndsAt: null },
    scoreboard: [],
    questionPoints: 16,
  });
  const [settings, setSettings] = useState(defaultSettings);
  const [startError, setStartError] = useState('');
  const [timeLeftMs, setTimeLeftMs] = useState(0);
  const [finalRevealCount, setFinalRevealCount] = useState(0);

  useEffect(() => {
    if (!socket) return undefined;

    const onState = (incoming) => {
      setState((prev) => ({ ...prev, ...incoming }));
    };

    const onPacks = ({ packs }) => {
      setState((prev) => ({ ...prev, availablePacks: packs || [] }));
    };

    const onRejected = ({ reason }) => {
      setStartError(reason || 'Unable to start Set The Bar.');
    };

    socket.on('setTheBarState', onState);
    socket.on('setTheBarPacks', onPacks);
    socket.on('setTheBarStartRejected', onRejected);

    socket.emit('setTheBarSyncState', { sessionId });
    socket.emit('setTheBarGetPacks', { sessionId });

    return () => {
      socket.off('setTheBarState', onState);
      socket.off('setTheBarPacks', onPacks);
      socket.off('setTheBarStartRejected', onRejected);
    };
  }, [socket, sessionId]);

  useEffect(() => {
    if (state.phase === 'lobby') {
      setSettings((prev) => ({
        ...prev,
        ...state.settings,
      }));
    }
  }, [state.phase, state.settings]);

  useEffect(() => {
    const targetEndsAt = state.phase === 'question'
      ? state.currentQuestion?.endsAt
      : state.phase === 'review-vote'
        ? state.review?.voteEndsAt
        : state.phase === 'intermission'
          ? state.progress?.intermissionEndsAt
          : null;

    if (!targetEndsAt) {
      setTimeLeftMs(0);
      return undefined;
    }

    const tick = () => {
      setTimeLeftMs(Math.max(targetEndsAt - Date.now(), 0));
    };

    tick();
    const intervalId = setInterval(tick, 100);
    return () => clearInterval(intervalId);
  }, [state.phase, state.currentQuestion?.endsAt, state.review?.voteEndsAt, state.progress?.intermissionEndsAt]);

  useEffect(() => {
    if (state.phase !== 'leaderboard') {
      setFinalRevealCount(0);
      return undefined;
    }

    const rows = sortScoreboard(state.scoreboard);
    const timerIds = rows.map((_, index) => setTimeout(() => {
      setFinalRevealCount(index + 1);
    }, (index + 1) * 400));

    return () => {
      timerIds.forEach((timerId) => clearTimeout(timerId));
    };
  }, [state.phase, state.scoreboard]);

  const scoreboard = useMemo(() => sortScoreboard(state.scoreboard), [state.scoreboard]);
  const isProductionServer = serverType === 'fayaz.fly.dev';
  const isThemePlaying = state.phase !== 'lobby' && state.phase !== 'leaderboard';

  const startGame = () => {
    setStartError('');
    socket.emit('setTheBarStartGame', {
      sessionId,
      settings,
    });
  };

  return (
    <div className="stb-host-root">
      <AudioPlayer audioSrc={setTheBarTheme} isPlaying={isThemePlaying} isMuted={isMusicMuted} />
      <div className="title-bar">
        <div className="join-message">
          <h2>
            {isProductionServer
              ? 'Join at Fayaz.One and choose Production.'
              : <>Join at <span className="red-text">Fayaz.One</span> and enter your room code.</>}
          </h2>
        </div>
        <div className="room-info">
          {!isProductionServer && <h2>Room Code: {ipAddress}</h2>}
          <h4>Session: {sessionId}</h4>
        </div>
      </div>

      {state.phase === 'lobby' ? (
        <div className="lobby-container">
          <AnimatedTitle title="Set The Bar" />

          <div className="app-container">
            <div className="players-container">
              <h4>Players:</h4>
              {(players || []).map((player) => (
                <button
                  key={player.socketId}
                  className="ghost-button"
                  onClick={() => {
                    setForceRemove(true);
                    removePlayer(player.socketId, true);
                  }}
                >
                  {player.name}
                </button>
              ))}
            </div>

            <div className="settings stb-host-settings">
              <h2>Game Settings</h2>

              <label htmlFor="stb-question-count">Questions per match</label>
              <select
                id="stb-question-count"
                value={settings.questionCount}
                onChange={(event) => setSettings((prev) => ({ ...prev, questionCount: Number(event.target.value) }))}
              >
                <option value={5}>5 questions</option>
                <option value={10}>10 questions</option>
                <option value={15}>15 questions</option>
              </select>

              <label htmlFor="stb-question-duration">Question length</label>
              <select
                id="stb-question-duration"
                value={settings.questionDurationSec}
                onChange={(event) => setSettings((prev) => ({ ...prev, questionDurationSec: Number(event.target.value) }))}
              >
                <option value={10}>10 seconds</option>
                <option value={20}>20 seconds</option>
                <option value={30}>30 seconds</option>
              </select>

              <label htmlFor="stb-pack">Question pack</label>
              <select
                id="stb-pack"
                value={settings.packId}
                onChange={(event) => setSettings((prev) => ({ ...prev, packId: event.target.value }))}
              >
                <option value="all">All Packs (mixed)</option>
                {(state.availablePacks || []).map((pack) => (
                  <option key={pack.id} value={pack.id}>
                    {pack.title} ({pack.questionCount})
                  </option>
                ))}
              </select>

              <p className="stb-host-fineprint">
                Correct answers earn full points. Wrong answers can earn points back if the room misidentifies the author.
              </p>

              <p className="stb-host-fineprint">
                Points per question: <strong>{formatIq(state.questionPoints)}</strong>
              </p>
            </div>
          </div>

          {startError && <p className="stb-host-error">{startError}</p>}

          <button
            className="start-game primary-button"
            onClick={startGame}
            disabled={(players || []).length < 2}
          >
            {(players || []).length < 2 ? 'Need at least 2 players' : 'Start Set The Bar'}
          </button>
        </div>
      ) : (
        <div className="stb-host-layout">
          <aside className="stb-host-scoreboard">
            <h3>Leaderboard</h3>
            <p className="stb-host-phase">Phase: {state.phase.replace('-', ' ')}</p>
            {scoreboard.map((entry, index) => (
              <div key={entry.id} className="stb-host-score-row">
                <span>#{index + 1} {entry.name}</span>
                <strong>{formatIq(entry.iq)} / 160</strong>
              </div>
            ))}
          </aside>

          <main className="stb-host-stage">
            {state.phase === 'question' && state.currentQuestion && (
              <div className="stb-panel">
                <div className="stb-host-row">
                  <span className="stb-pill">Question {state.currentQuestion.index} / {state.currentQuestion.total}</span>
                  <span className="stb-pill">{(timeLeftMs / 1000).toFixed(1)}s</span>
                </div>

                <h2>{state.currentQuestion.prompt}</h2>
                <p className="stb-host-meta">{state.currentQuestion.category} · {state.currentQuestion.type}</p>

                <div className="stb-option-grid">
                  {(state.currentQuestion.options || []).map((option) => (
                    <div key={option.id} className="stb-option-card">
                      <span className="stb-option-tag">{option.id}</span>
                      <span>{option.text}</span>
                    </div>
                  ))}
                </div>

                <p className="stb-host-meta">
                  {state.currentQuestion.answeredCount} / {(players || []).length} locked in
                </p>
              </div>
            )}

            {state.phase === 'intermission' && (
              <div className="stb-panel stb-center">
                <h2>Next question incoming</h2>
                <p>{(timeLeftMs / 1000).toFixed(1)}s</p>
              </div>
            )}

            {state.phase === 'review-stats' && state.review && (
              <div className="stb-panel">
                <h2>Question {state.review.questionIndex} Results</h2>
                <p className="stb-host-review-prompt">{state.review.questionPrompt}</p>
                <div className="stb-stat-grid">
                  <div className="stb-stat-card success">
                    <h3>{state.review.stats?.percentRight || 0}%</h3>
                    <p>Got it right</p>
                  </div>
                  <div className="stb-stat-card danger">
                    <h3>{state.review.stats?.percentWrong || 0}%</h3>
                    <p>Got it wrong</p>
                  </div>
                </div>
                <p className="stb-host-meta">
                  Answered: {state.review.stats?.answeredCount || 0} · Wrong answers to investigate: {state.review.wrongAnswerCount}
                </p>
              </div>
            )}

            {state.phase === 'review-vote' && state.review?.currentEntry && (
              <div className="stb-panel">
                <div className="stb-host-row">
                  <span className="stb-pill">Detective Vote</span>
                  <span className="stb-pill">{(timeLeftMs / 1000).toFixed(1)}s</span>
                </div>
                <h2>Who wrote this wrong answer?</h2>
                <div className="stb-anon-answer">“{state.review.currentEntry.answerText}”</div>
                <p className="stb-host-meta">
                  Votes in: {state.review.voteProgress?.submittedVotes || 0} / {state.review.voteProgress?.totalVoters || 0}
                </p>
              </div>
            )}

            {state.phase === 'review-reveal' && state.review?.lastReveal && (
              <div className="stb-panel stb-reveal">
                <h2>Reveal</h2>
                <p className="stb-host-review-prompt">Wrong answer: “{state.review.lastReveal.answerText}”</p>
                <h3>{state.review.lastReveal.accusedPlayerName} wrote it.</h3>
                <p>
                  {state.review.lastReveal.wrongVotes} / {state.review.lastReveal.totalVoters} voted wrong.
                </p>
                <p className="stb-refund">
                  IQ Refund: +{formatIq(state.review.lastReveal.refundPoints)} ({Math.round((state.review.lastReveal.refundRatio || 0) * 100)}%)
                </p>
              </div>
            )}

            {state.phase === 'review-correct' && state.review && (
              <div className="stb-panel stb-center">
                <h2>Correct Answer</h2>
                <p className="stb-correct">{state.review.correctAnswerText}</p>
              </div>
            )}

            {state.phase === 'leaderboard' && (
              <div className="stb-panel">
                <h2>Final IQ Board</h2>
                <div className="stb-final-list">
                  {scoreboard.map((entry, index) => (
                    <div
                      key={entry.id}
                      className={`stb-final-row ${index < finalRevealCount ? 'show' : ''}`}
                    >
                      <span>#{index + 1} {entry.name}</span>
                      <strong>{formatIq(entry.iq)} / 160 IQ</strong>
                    </div>
                  ))}
                </div>
                <div className="stb-actions">
                  <button className="primary-button" onClick={startGame}>Play Again</button>
                  <button className="ghost-button" onClick={onReturnToPartyPack}>Return to Party Pack</button>
                </div>
              </div>
            )}
          </main>
        </div>
      )}
    </div>
  );
}
